import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create the context
const MetaContext = createContext(null);

// Provider component
export function MetaProvider({ children }) {
  const [metaStatus, setMetaStatus] = useState(null);
  const [credentials, setCredentials] = useState({ accessToken: "", adAccountId: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Meta status - memoized to prevent unnecessary re-renders
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meta/status`);
      setMetaStatus(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error("Error fetching Meta status:", err);
      setMetaStatus({ connected: false });
      setError(err.message);
      return { connected: false };
    }
  }, []);

  // Fetch saved credentials
  const fetchCredentials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/meta`);
      if (response.data) {
        setCredentials({
          accessToken: response.data.access_token || "",
          adAccountId: response.data.ad_account_id || ""
        });
        // Also update status from response if available
        if (response.data.connected !== undefined) {
          setMetaStatus({
            connected: response.data.connected,
            account_name: response.data.account_name
          });
        }
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching credentials:", err);
      return null;
    }
  }, []);

  // Save credentials and update status
  const saveCredentials = useCallback(async (accessToken, adAccountId) => {
    try {
      const response = await axios.post(`${API}/settings/meta`, {
        access_token: accessToken,
        ad_account_id: adAccountId
      });

      if (response.data.success) {
        // Update local state immediately
        setCredentials({ accessToken, adAccountId });
        setMetaStatus({
          connected: true,
          account_name: response.data.account_name
        });
        return { success: true, account_name: response.data.account_name };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (err) {
      console.error("Error saving credentials:", err);
      return { success: false, error: err.message };
    }
  }, []);

  // Test connection without saving
  const testConnection = useCallback(async (accessToken, adAccountId) => {
    try {
      const response = await axios.post(`${API}/settings/meta/test`, {
        access_token: accessToken,
        ad_account_id: adAccountId
      });
      return response.data;
    } catch (err) {
      console.error("Error testing connection:", err);
      return { valid: false, error: err.message };
    }
  }, []);

  // Refresh status (can be called manually)
  const refreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      await fetchStatus();
    } finally {
      setLoading(false);
    }
  }, [fetchStatus]);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await fetchStatus();
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [fetchStatus]);

  const value = {
    metaStatus,
    credentials,
    loading,
    error,
    fetchStatus,
    fetchCredentials,
    saveCredentials,
    testConnection,
    refreshStatus,
    setMetaStatus
  };

  return (
    <MetaContext.Provider value={value}>
      {children}
    </MetaContext.Provider>
  );
}

// Custom hook to use the context
export function useMeta() {
  const context = useContext(MetaContext);
  if (!context) {
    throw new Error("useMeta must be used within a MetaProvider");
  }
  return context;
}

export default MetaContext;
